import React, { useState } from 'react';
import './CuadranteGrid.scss';

const CuadranteGrid = ({ cuadrantes, año, mes }) => {
  const [selectedCuadrante, setSelectedCuadrante] = useState(null);

  // Obtener el número de días del mes
  const getDiasDelMes = (año, mes) => {
    return new Date(año, mes, 0).getDate();
  };

  // Obtener el día de la semana del primer día del mes (0 = domingo, 1 = lunes, etc.)
  const getPrimerDiaSemana = (año, mes) => {
    const dia = new Date(año, mes - 1, 1).getDay();
    // Convertir a formato europeo (0 = lunes, 6 = domingo)
    return dia === 0 ? 6 : dia - 1;
  };

  const diasSemana = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  const mesesNombres = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Agrupar cuadrantes por persona
  const cuadrantesPorPersona = {};
  cuadrantes.forEach(cuadrante => {
    const persona = cuadrante.CUADT_PERSONA;
    const nombreCompleto = cuadrante.VSSPER_NOM 
      ? `${cuadrante.VSSPER_NOM} ${cuadrante.VSSPER_APELL1} ${cuadrante.VSSPER_APELL2}`
      : `Persona ${persona}`;
    
    if (!cuadrantesPorPersona[persona]) {
      cuadrantesPorPersona[persona] = {
        nombre: nombreCompleto,
        cuadrantes: []
      };
    }
    cuadrantesPorPersona[persona].cuadrantes.push(cuadrante);
  });

  const renderGrid = (personaData) => {
    const diasMes = getDiasDelMes(año, mes);
    const primerDia = getPrimerDiaSemana(año, mes);
    const totalCeldas = Math.ceil((diasMes + primerDia) / 7) * 7;

    return (
      <div className="calendario-grid">
        {/* Encabezado con días de la semana */}
        <div className="grid-header">
          {diasSemana.map((dia, index) => (
            <div key={index} className="dia-semana">
              {dia}
            </div>
          ))}
        </div>

        {/* Grid de días */}
        <div className="grid-body">
          {Array.from({ length: totalCeldas }).map((_, index) => {
            const dia = index - primerDia + 1;
            const esDiaValido = dia > 0 && dia <= diasMes;
            
            // Buscar cuadrantes para este día
            const cuadrantesDelDia = esDiaValido 
              ? personaData.cuadrantes.filter(c => {
                  // Aquí podrías tener lógica más específica si los datos incluyen días
                  return true; // Por ahora mostramos info general del mes
                })
              : [];

            const esFestivo = esDiaValido && cuadrantesDelDia.some(c => c.CUADT_HHFEST > 0);
            const esFinDeSemana = esDiaValido && (index % 7 === 5 || index % 7 === 6);

            return (
              <div
                key={index}
                className={`dia-celda ${!esDiaValido ? 'vacio' : ''} ${esFestivo ? 'festivo' : ''} ${esFinDeSemana ? 'fin-semana' : ''}`}
              >
                {esDiaValido && (
                  <>
                    <div className="numero-dia">{dia}</div>
                    {cuadrantesDelDia.length > 0 && (
                      <div className="info-dia">
                        <div className="horas-totales">
                          {cuadrantesDelDia[0].CUADT_HHTOT || 0}h
                        </div>
                        {cuadrantesDelDia[0].CUADT_TURNOPER && (
                          <div className="turno-numero">
                            T{cuadrantesDelDia[0].CUADT_TURNOPER}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (!cuadrantes || cuadrantes.length === 0) {
    return (
      <div className="cuadrante-grid-empty">
        <p>No hay cuadrantes para mostrar</p>
      </div>
    );
  }

  return (
    <div className="cuadrante-grid">
      <div className="grid-header-main">
        <h2>📅 Cuadrantes - {mesesNombres[mes - 1]} {año}</h2>
        <div className="legend">
          <span className="legend-item">
            <span className="legend-color festivo"></span>
            Festivo
          </span>
          <span className="legend-item">
            <span className="legend-color fin-semana"></span>
            Fin de semana
          </span>
        </div>
      </div>

      {Object.entries(cuadrantesPorPersona).map(([persona, data]) => (
        <div key={persona} className="persona-grid-section">
          <div className="persona-grid-header">
            <h3>{data.nombre}</h3>
            <div className="resumen-horas">
              <span>Total horas mes: </span>
              <strong>
                {data.cuadrantes.reduce((sum, c) => sum + (c.CUADT_HHTOT || 0), 0)}h
              </strong>
            </div>
          </div>
          {renderGrid(data)}
          
          {/* Detalle de turnos */}
          <div className="detalle-turnos">
            <h4>📊 Resumen de Turnos</h4>
            <div className="turnos-lista">
              {data.cuadrantes.map((cuadrante, idx) => (
                <div 
                  key={idx} 
                  className="turno-item"
                  onClick={() => setSelectedCuadrante(cuadrante)}
                >
                  <span className="turno-nombre">Turno {cuadrante.CUADT_TURNOPER}</span>
                  <span className="turno-horas">{cuadrante.CUADT_HHTOT || 0}h totales</span>
                  {cuadrante.CUADT_HHFEST > 0 && (
                    <span className="turno-festivo">{cuadrante.CUADT_HHFEST}h festivas</span>
                  )}
                  {cuadrante.CUADT_HHNOC > 0 && (
                    <span className="turno-nocturno">{cuadrante.CUADT_HHNOC}h nocturnas</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}

      {/* Modal con detalles */}
      {selectedCuadrante && (
        <div className="modal-overlay" onClick={() => setSelectedCuadrante(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Detalle del Turno</h3>
              <button onClick={() => setSelectedCuadrante(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detalle-grid">
                <div className="detalle-item">
                  <span className="label">Turno/Operación:</span>
                  <span className="value">{selectedCuadrante.CUADT_TURNOPER}</span>
                </div>
                <div className="detalle-item">
                  <span className="label">Horas Totales:</span>
                  <span className="value">{selectedCuadrante.CUADT_HHTOT}h</span>
                </div>
                {selectedCuadrante.CUADT_HHTOTB && (
                  <div className="detalle-item">
                    <span className="label">Horas Base:</span>
                    <span className="value">{selectedCuadrante.CUADT_HHTOTB}h</span>
                  </div>
                )}
                {selectedCuadrante.CUADT_HHFEST > 0 && (
                  <div className="detalle-item">
                    <span className="label">Horas Festivas:</span>
                    <span className="value">{selectedCuadrante.CUADT_HHFEST}h</span>
                  </div>
                )}
                {selectedCuadrante.CUADT_HHNOC > 0 && (
                  <div className="detalle-item">
                    <span className="label">Horas Nocturnas:</span>
                    <span className="value">{selectedCuadrante.CUADT_HHNOC}h</span>
                  </div>
                )}
                <div className="detalle-item">
                  <span className="label">Contrato:</span>
                  <span className="value">{selectedCuadrante.CUADT_CTRT}/{selectedCuadrante.CUADT_SERV}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuadranteGrid;














